import { DragEvent, useEffect, useMemo, useState } from 'react';
import { HeroNumber } from '@/components/HeroNumber';
import {
  Facebook,
  CheckCircle2,
  XCircle,
  Upload,
  RefreshCw,
  TrendingUp,
} from 'lucide-react';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { useCampaigns, type Campaign } from '@/hooks/useCampaigns';
import { useAuth } from '@/contexts/AuthContext';

// ── A/B testing types ─────────────────────────────────────────────────────────

interface AbVariant {
  id: string;
  label: string;
  description: string;
  template: string;
}

interface AbVariantResult extends AbVariant {
  sent: number;
  replied: number;
  quoted: number;
  replyRate: string | null;
  quoteRate: string | null;
  avgReplyMinutes: number | null;
}

interface AbTestWithResults {
  id: string;
  test_name: string;
  test_type: string;
  status: string;
  winner_variant: string | null;
  min_sample_size: number;
  variants: AbVariant[];
  variantResults: AbVariantResult[];
}

async function loadAbResults(partnerId: string): Promise<AbTestWithResults[]> {
  if (!isSupabaseConfigured) return [];

  const { data: tests } = await supabase
    .from('semmy_ab_tests')
    .select('*')
    .eq('partner_id', partnerId)
    .order('created_at', { ascending: false });

  if (!tests?.length) return [];

  return Promise.all(
    tests.map(async (test) => {
      const { data: rows } = await supabase
        .from('semmy_ab_results')
        .select('variant_id, replied_at, quoted_at, time_to_reply_minutes')
        .eq('test_id', test.id);

      const variantResults: AbVariantResult[] = (test.variants as AbVariant[]).map((v) => {
        const vRows = (rows ?? []).filter((r) => r.variant_id === v.id);
        const sent    = vRows.length;
        const replied = vRows.filter((r) => r.replied_at).length;
        const quoted  = vRows.filter((r) => r.quoted_at).length;
        const replyTimes = vRows
          .map((r) => r.time_to_reply_minutes)
          .filter((t): t is number => t != null && t > 0);
        const avgReplyMinutes = replyTimes.length
          ? Math.round(replyTimes.reduce((a, b) => a + b, 0) / replyTimes.length)
          : null;
        return {
          ...v,
          sent,
          replied,
          quoted,
          replyRate: sent > 0 ? ((replied / sent) * 100).toFixed(1) : null,
          quoteRate: replied > 0 ? ((quoted / replied) * 100).toFixed(1) : null,
          avgReplyMinutes,
        };
      });

      return { ...test, variantResults } as AbTestWithResults;
    })
  );
}

function AbTestCard({ test }: { test: AbTestWithResults }) {
  const isActive   = test.status === 'active';
  const totalSent  = test.variantResults.reduce((s, v) => s + v.sent, 0);
  const target     = test.min_sample_size * test.variantResults.length;
  const progress   = Math.min(100, (totalSent / target) * 100);

  return (
    <div style={{
      border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border-primary)'}`,
      borderRadius: '12px',
      padding: '16px',
      background: 'var(--bg-surface-raised)',
      marginBottom: '10px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>
            {test.test_name.replace(/_/g, ' ')}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
            {isActive
              ? `Actief · ${totalSent} lead${totalSent !== 1 ? 's' : ''} verstuurd`
              : `Afgerond · winnaar: Variant ${test.winner_variant}`}
          </div>
        </div>
        {isActive && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {totalSent} / {target} leads
            </div>
            <div style={{ width: '80px', height: '4px', background: 'var(--border-separator)', borderRadius: '2px', marginTop: '4px' }}>
              <div style={{ width: `${progress}%`, height: '100%', background: 'var(--accent)', borderRadius: '2px', transition: 'width 0.3s' }} />
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${test.variantResults.length}, 1fr)`, gap: '8px' }}>
        {test.variantResults.map((v) => {
          const myRate    = parseFloat(v.replyRate ?? '-1');
          const isLeading = v.sent > 3 && test.variantResults.every(
            (other) => other.id === v.id || myRate >= parseFloat(other.replyRate ?? '-1')
          );
          return (
            <div key={v.id} style={{
              background: isLeading ? 'var(--accent-dim, rgba(var(--accent-rgb,59,130,246),0.08))' : 'var(--bg-input)',
              border: `1px solid ${isLeading ? 'var(--accent)' : 'var(--border-separator)'}`,
              borderRadius: '8px',
              padding: '10px 12px',
            }}>
              <div style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '2px' }}>
                Variant {v.id} — {v.label}
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '8px' }}>
                {v.description}
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '20px', fontWeight: 600, color: isLeading ? 'var(--accent)' : 'var(--text-primary)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                    {v.replyRate != null ? `${v.replyRate}%` : '—'}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>reply rate</div>
                </div>
                <div>
                  <div style={{ fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                    {v.sent}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>verstuurd</div>
                </div>
                {v.avgReplyMinutes != null && (
                  <div>
                    <div style={{ fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                      {v.avgReplyMinutes}m
                    </div>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>gem. reactietijd</div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {isActive && totalSent < target && (
        <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '10px', fontStyle: 'italic', marginBottom: 0 }}>
          Nog {target - totalSent} lead{(target - totalSent) !== 1 ? 's' : ''} nodig voor een betrouwbare uitkomst. Resultaten nu zijn indicatief.
        </p>
      )}
    </div>
  );
}

// ── Creatives types ───────────────────────────────────────────────────────────

type CampaignPlatform = 'meta' | 'google';
type UploadedCreativeStatus = 'in_review' | 'goedgekeurd' | 'afgewezen';

interface PendingCreative {
  id: string;
  copy: string;
  platform: CampaignPlatform;
  targeting: string;
  image_url: string | null;
}

interface UploadedCreative {
  id: string;
  filename: string;
  status: UploadedCreativeStatus;
  image_url: string | null;
}

const MOCK_PENDING_CREATIVES: PendingCreative[] = [
  {
    id: 'cre-001',
    copy: 'Droomvloer voor je woonkamer? Onze PVC visgraat vloeren zijn nu beschikbaar in 20+ kleuren. Gratis inmeting en vrijblijvende offerte.',
    platform: 'meta',
    targeting: 'Nederland, 28-60 jaar, interesse: interieur, vloeren, verbouwing',
    image_url: null,
  },
];

const MOCK_UPLOADED_CREATIVES: UploadedCreative[] = [
  { id: 'upc-001', filename: 'vloeren_ad_1.jpg',   status: 'goedgekeurd', image_url: null },
  { id: 'upc-002', filename: 'pvc_creative_2.jpg',  status: 'in_review',  image_url: null },
  { id: 'upc-003', filename: 'vloer_adv_3.jpg',     status: 'afgewezen',  image_url: null },
];

const PLATFORM_LABELS: Record<CampaignPlatform, string> = {
  meta: 'Meta',
  google: 'Google',
};

const creativeStatusStyles: Record<UploadedCreativeStatus, { background: string; color: string; border: string }> = {
  in_review:   { background: 'var(--warning-bg)',  color: 'var(--warning-text)',  border: 'var(--warning-border)' },
  goedgekeurd: { background: 'var(--success-bg)',  color: 'var(--success-text)',  border: 'var(--success-border)' },
  afgewezen:   { background: 'var(--danger-bg)',   color: 'var(--danger-text)',   border: 'var(--danger-border)' },
};

const CREATIVE_STATUS_LABELS: Record<UploadedCreativeStatus, string> = {
  in_review:   'In review',
  goedgekeurd: 'Goedgekeurd',
  afgewezen:   'Afgewezen',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatEur(value: number) {
  return `€${value.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function truncate(text: string, length: number) {
  if (text.length <= length) return text;
  return `${text.slice(0, length).trim()}…`;
}

const CAMPAIGN_STATUS: Record<string, { label: string; bg: string; color: string; border: string }> = {
  active:   { label: 'Actief',     bg: 'var(--success-bg)',  color: 'var(--success-text)',  border: 'var(--success-border)' },
  paused:   { label: 'Gepauzeerd', bg: 'var(--warning-bg)',  color: 'var(--warning-text)',  border: 'var(--warning-border)' },
  archived: { label: 'Archief',    bg: 'var(--bg-input)',    color: 'var(--text-tertiary)', border: 'var(--border-separator)' },
  inactive: { label: 'Inactief',   bg: 'var(--bg-input)',    color: 'var(--text-tertiary)', border: 'var(--border-separator)' },
};

// ── MetricCard ────────────────────────────────────────────────────────────────

function MetricCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{
      background: 'var(--bg-surface-raised)',
      border: '1px solid var(--border-separator)',
      borderRadius: 14,
      padding: '16px 18px',
    }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginBottom: 8 }}>
        {label}
      </div>
      <div style={{
        fontSize: 22, fontWeight: 700, color: 'var(--text-primary)',
        letterSpacing: '-0.03em', lineHeight: 1, fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );
}

// ── FunnelBar ─────────────────────────────────────────────────────────────────

function FunnelBar({ label, value, max }: { label: string; value: number; max: number }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3 }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</span>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
          {value.toLocaleString('nl-NL')}
        </span>
      </div>
      <div style={{ height: 3, background: 'var(--border-separator)', borderRadius: 99 }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: 'var(--accent)', borderRadius: 99,
          transition: 'width 0.5s cubic-bezier(0.16,1,0.3,1)',
        }} />
      </div>
    </div>
  );
}

// ── CampaignCard ──────────────────────────────────────────────────────────────

function CampaignCard({ c, maxImpressions, maxReach, maxLeads, maxBooked }: {
  c: Campaign;
  maxImpressions: number;
  maxReach: number;
  maxLeads: number;
  maxBooked: number;
}) {
  const statusInfo = CAMPAIGN_STATUS[c.status] ?? CAMPAIGN_STATUS['inactive'];
  const heroValue  = c.cost_per_booking_eur != null && c.booked_count > 0
    ? formatEur(c.cost_per_booking_eur)
    : c.cpl_eur != null && c.leads_count > 0
      ? formatEur(c.cpl_eur)
      : formatEur(c.spend_eur);
  const heroLabel  = c.cost_per_booking_eur != null && c.booked_count > 0
    ? 'per boeking'
    : c.cpl_eur != null && c.leads_count > 0
      ? 'CPL'
      : 'uitgave';

  return (
    <div style={{
      border: '1px solid var(--border-separator)',
      borderRadius: 16,
      overflow: 'hidden',
      background: 'var(--bg-surface-raised)',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Creative preview */}
      {c.creative_image_url ? (
        <div style={{ height: 140, overflow: 'hidden', flexShrink: 0 }}>
          <img
            src={c.creative_image_url}
            alt={c.ad_name ?? c.campaign_name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      ) : (
        <div style={{
          height: 80, flexShrink: 0,
          background: 'var(--bg-surface)',
          borderBottom: '1px solid var(--border-separator)',
          display: 'grid', placeItems: 'center',
        }}>
          <Facebook size={28} style={{ color: 'var(--text-muted)' }} />
        </div>
      )}

      <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.4,
              display: '-webkit-box', overflow: 'hidden',
              WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            }}>
              {c.campaign_name}
            </div>
            {c.creative_headline && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                {truncate(c.creative_headline, 60)}
              </div>
            )}
          </div>
          <span style={{
            fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0,
            padding: '3px 8px', borderRadius: 99,
            background: statusInfo.bg, color: statusInfo.color, border: `1px solid ${statusInfo.border}`,
          }}>
            {statusInfo.label}
          </span>
        </div>

        {/* Funnel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
          <FunnelBar label="Vertoningen" value={c.impressions}  max={maxImpressions} />
          <FunnelBar label="Bereik"      value={c.reach}        max={maxReach} />
          <FunnelBar label="Leads"       value={c.leads_count}  max={maxLeads} />
          <FunnelBar label="Boekingen"   value={c.booked_count} max={Math.max(maxBooked, 1)} />
        </div>

        {/* Hero metric */}
        <div style={{
          borderTop: '1px solid var(--border-separator)', paddingTop: 12,
          display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 'auto',
        }}>
          <span style={{
            fontSize: 20, fontWeight: 700, color: 'var(--accent)',
            letterSpacing: '-0.03em', lineHeight: 1, fontVariantNumeric: 'tabular-nums',
          }}>
            {heroValue}
          </span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{heroLabel}</span>
        </div>
      </div>
    </div>
  );
}

// ── EmptyCampaigns ────────────────────────────────────────────────────────────

function EmptyCampaigns({ onSync, syncing }: { onSync: () => void; syncing: boolean }) {
  return (
    <div style={{
      border: '1px dashed var(--border-separator)',
      borderRadius: 16, padding: '48px 24px',
      textAlign: 'center', background: 'var(--bg-surface-raised)',
    }}>
      <div style={{ marginBottom: 16, opacity: 0.25 }}>
        <TrendingUp size={40} style={{ margin: '0 auto' }} />
      </div>
      <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>
        Geen campagnedata beschikbaar
      </p>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', maxWidth: 320, margin: '0 auto 20px', lineHeight: 1.6 }}>
        Koppel je Facebook Business account via Instellingen of synchroniseer handmatig om campagneprestaties te zien.
      </p>
      <button
        type="button"
        onClick={onSync}
        disabled={syncing}
        className="btn btn-primary"
        style={{ opacity: syncing ? 0.7 : 1 }}
      >
        <RefreshCw size={13} style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }} />
        {syncing ? 'Synchroniseren…' : 'Sync campagnes'}
      </button>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PartnerAdsPage() {
  const { effectivePartnerId, effectiveRole } = useAuth();
  // Operators and account managers see all campaigns; RLS enforces the boundary server-side.
  // Partners are scoped to their own partner_id via both the query filter and RLS.
  // DEV-ONLY: 'partner-003' fallback fires when previewing a partner role without a real
  // partner_id on the auth user — remove before going multi-tenant.
  const isOperatorView = effectiveRole === 'operator' || effectiveRole === 'account_manager';
  const partnerId = isOperatorView
    ? null
    : (effectivePartnerId ?? 'partner-003' /* DEV-ONLY fallback */);

  const { campaigns, loading, syncing, lastSynced, syncError, syncCampaigns } = useCampaigns(partnerId);
  const [sectionHovered, setSectionHovered] = useState(false);

  const [rejectionOpen, setRejectionOpen] = useState<Record<string, boolean>>({});
  const [rejectionFeedback, setRejectionFeedback] = useState<Record<string, string>>({});
  const [uploadedCreatives, setUploadedCreatives] = useState<UploadedCreative[]>(MOCK_UPLOADED_CREATIVES);
  const [dragActive, setDragActive] = useState(false);
  const [abTests, setAbTests] = useState<AbTestWithResults[]>([]);

  useEffect(() => {
    loadAbResults(partnerId).then(setAbTests).catch(() => {});
  }, [partnerId]);

  const maxImpressions = useMemo(() => Math.max(1, ...campaigns.map(c => c.impressions)), [campaigns]);
  const maxReach       = useMemo(() => Math.max(1, ...campaigns.map(c => c.reach)),       [campaigns]);
  const maxLeads       = useMemo(() => Math.max(1, ...campaigns.map(c => c.leads_count)), [campaigns]);
  const maxBooked      = useMemo(() => Math.max(1, ...campaigns.map(c => c.booked_count)), [campaigns]);

  const totalSpend  = useMemo(() => campaigns.reduce((s, c) => s + c.spend_eur, 0),   [campaigns]);
  const totalLeads  = useMemo(() => campaigns.reduce((s, c) => s + c.leads_count, 0), [campaigns]);
  const totalBooked = useMemo(() => campaigns.reduce((s, c) => s + c.booked_count, 0), [campaigns]);
  const avgCpl      = totalLeads > 0 ? totalSpend / totalLeads : null;
  const avgCpb      = totalBooked > 0 ? totalSpend / totalBooked : null;
  const convPct     = totalLeads > 0 ? (totalBooked / totalLeads) * 100 : null;

  const handleUpload = (files: FileList | null) => {
    if (!files) return;
    const newItems = Array.from(files).slice(0, 3).map((file) => ({
      id: `upload-${Date.now()}-${file.name}`,
      filename: file.name,
      status: 'in_review' as const,
      image_url: URL.createObjectURL(file),
    }));
    setUploadedCreatives((prev) => [...newItems, ...prev]);
  };

  const onApprove = (creative: PendingCreative) => {
    console.log('Goedkeuren:', creative.id, creative.copy);
  };

  const onReject = (creative: PendingCreative) => {
    setRejectionOpen((prev) => ({ ...prev, [creative.id]: !prev[creative.id] }));
  };

  const onSubmitRejection = (creative: PendingCreative) => {
    console.log('Afgewezen:', creative.id, rejectionFeedback[creative.id] || 'Geen feedback');
    setRejectionOpen((prev) => ({ ...prev, [creative.id]: false }));
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    handleUpload(event.dataTransfer.files);
  };

  const previewCreatives = useMemo(() => uploadedCreatives.slice(0, 5), [uploadedCreatives]);

  const lastSyncedLabel = lastSynced
    ? new Date(lastSynced).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
          Bekijk campagneprestaties, controleer nieuwe creatives en upload advertentiemateriaal.
        </p>
      </div>

      {/* ── Campaigns ── */}
      <section
        onMouseEnter={() => setSectionHovered(true)}
        onMouseLeave={() => setSectionHovered(false)}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Campagnes</h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
              Prestaties per Facebook-campagne — leads, bereik en kosten.
              {lastSyncedLabel && (
                <span style={{ marginLeft: 8, opacity: 0.6 }}>Gesynchroniseerd: {lastSyncedLabel}</span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={syncCampaigns}
            disabled={syncing}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 14px', borderRadius: 99,
              background: 'var(--bg-surface-raised)',
              border: '1px solid var(--border-separator)',
              fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)',
              cursor: syncing ? 'default' : 'pointer',
              opacity: sectionHovered ? 1 : 0,
              transform: sectionHovered ? 'translateY(0)' : 'translateY(4px)',
              transition: 'opacity 0.15s, transform 0.15s',
              pointerEvents: sectionHovered ? 'auto' : 'none',
            }}
          >
            <RefreshCw
              size={12}
              style={{ animation: syncing ? 'spin 1s linear infinite' : 'none' }}
            />
            {syncing ? 'Syncing…' : 'Sync nu'}
          </button>
        </div>

        {syncError && (
          <div style={{
            padding: '10px 14px', borderRadius: 10, marginBottom: 14,
            background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.2)',
            fontSize: 12, color: '#b91c1c',
          }}>
            Sync mislukt: {syncError}
          </div>
        )}

        {/* Metric summary */}
        {campaigns.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 }}>
            <MetricCard label="Uitgave"         value={formatEur(totalSpend)} />
            <MetricCard label="Leads"            value={totalLeads.toLocaleString('nl-NL')} />
            <div style={{
              padding: '20px 24px',
              background: 'var(--accent-dim)',
              border: '1px solid var(--accent)',
              borderRadius: '8px',
              gridColumn: 'span 1',
            }}>
              <HeroNumber
                label="Kosten / lead"
                value={avgCpl ? `€${avgCpl.toFixed(2)}` : '—'}
                sub={`${totalLeads} leads gegenereerd`}
                size="lg"
                accent
              />
            </div>
            <MetricCard label="Boekingen"        value={totalBooked.toLocaleString('nl-NL')} />
            <MetricCard label="Kosten / boeking" value={avgCpb != null ? formatEur(avgCpb) : '—'} />
            <MetricCard label="Conversie"        value={convPct != null ? `${convPct.toFixed(1)}%` : '—'} />
          </div>
        )}

        {/* Campaign cards */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                height: 280, borderRadius: 16,
                background: 'var(--bg-surface-raised)',
                border: '1px solid var(--border-separator)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }} />
            ))}
          </div>
        ) : campaigns.length === 0 ? (
          <EmptyCampaigns onSync={syncCampaigns} syncing={syncing} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {campaigns.map(c => (
              <CampaignCard
                key={c.id}
                c={c}
                maxImpressions={maxImpressions}
                maxReach={maxReach}
                maxLeads={maxLeads}
                maxBooked={maxBooked}
              />
            ))}
          </div>
        )}
      </section>

      {/* ── Pending creatives ── */}
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Wacht op jouw goedkeuring</h2>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>Nieuwe creatives die klaarstaan voor jouw beoordeling.</p>
        </div>

        <div className="space-y-4">
          {MOCK_PENDING_CREATIVES.map((creative) => (
            <div key={creative.id} className="rounded-[24px] border p-5" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-separator)' }}>
              <div className="grid gap-4 lg:grid-cols-[220px_1fr]">
                <div className="h-[180px] rounded-3xl overflow-hidden flex items-center justify-center text-xs" style={{ background: 'var(--bg-input)', color: 'var(--text-muted)', border: '1px solid var(--border-separator)' }}>
                  {!creative.image_url ? 'Advertentie preview' : <img src={creative.image_url} alt="Creative" className="h-full w-full object-cover" />}
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{truncate(creative.copy, 120)}</p>
                    <p className="text-xs mt-3" style={{ color: 'var(--text-muted)' }}>
                      {PLATFORM_LABELS[creative.platform]} · {creative.targeting}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => onApprove(creative)}
                    >
                      Goedkeuren
                    </button>
                    <button
                      type="button"
                      className="btn btn-destructive"
                      onClick={() => onReject(creative)}
                    >
                      Afwijzen
                    </button>
                  </div>

                  {rejectionOpen[creative.id] && (
                    <div className="space-y-3 pt-3">
                      <textarea
                        value={rejectionFeedback[creative.id] || ''}
                        onChange={(event) => setRejectionFeedback((prev) => ({ ...prev, [creative.id]: event.target.value }))}
                        placeholder="Feedback voor Converze..."
                        className="w-full min-h-[120px] rounded-3xl border p-4 text-sm resize-none"
                        style={{ background: 'var(--bg-input)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
                      />
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="btn btn-destructive-confirm"
                          onClick={() => onSubmitRejection(creative)}
                        >
                          Verstuur feedback
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => setRejectionOpen((prev) => ({ ...prev, [creative.id]: false }))}
                        >
                          Annuleer
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Upload creatives ── */}
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Jouw creatives</h2>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>Upload nieuwe beelden of advertenties. Ze worden klaar gezet voor beoordeling.</p>
        </div>

        <div
          className={`rounded-[24px] border border-dashed p-6 text-center transition ${dragActive ? 'border-accent bg-[rgba(59,130,246,0.06)]' : 'bg-[var(--bg-surface-raised)]'}`}
          style={{ borderColor: dragActive ? 'var(--accent)' : 'var(--border-primary)' }}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
          onDrop={onDrop}
        >
          <div className="mx-auto flex h-[120px] w-[120px] items-center justify-center rounded-3xl bg-[rgba(59,130,246,0.08)] mb-4">
            <Upload size={32} />
          </div>
          <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Sleep een bestand hierheen of kies een bestand</p>
          <p className="text-sm mt-2" style={{ color: 'var(--text-tertiary)' }}>PNG, JPG of MP4.</p>
          <label className="mt-4 inline-flex cursor-pointer rounded-full px-5 py-2 text-sm font-semibold" style={{ background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}>
            Bladeren
            <input
              type="file"
              accept="image/*,video/*"
              className="hidden"
              multiple
              onChange={(event) => handleUpload(event.target.files)}
            />
          </label>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {previewCreatives.map((creative) => (
            <div key={creative.id} className="rounded-3xl border p-3 flex items-center gap-3" style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-separator)' }}>
              <div className="h-16 w-16 rounded-3xl overflow-hidden flex items-center justify-center text-[10px]" style={{ background: 'var(--bg-input)', color: 'var(--text-muted)', border: '1px solid var(--border-separator)' }}>
                {creative.image_url ? <img src={creative.image_url} alt={creative.filename} className="h-full w-full object-cover" /> : 'Preview'}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{creative.filename}</p>
                <span className="inline-flex items-center gap-1 mt-1 rounded-full px-2.5 py-1 text-[11px] font-semibold" style={{
                  background: creativeStatusStyles[creative.status].background,
                  color: creativeStatusStyles[creative.status].color,
                  border: `1px solid ${creativeStatusStyles[creative.status].border}`,
                }}>
                  {creative.status === 'goedgekeurd' ? <CheckCircle2 size={12} /> : creative.status === 'afgewezen' ? <XCircle size={12} /> : null}
                  {CREATIVE_STATUS_LABELS[creative.status]}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── A/B tests ── */}
      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>A/B Tests</h2>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
            Welke openingsboodschap converteert het beste? Semmy test dit automatisch op elke nieuwe lead.
          </p>
        </div>

        {abTests.length === 0 ? (
          <div style={{ border: '1px solid var(--border-separator)', borderRadius: '12px', padding: '24px', background: 'var(--bg-surface-raised)', textAlign: 'center' }}>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Geen actieve tests gevonden.</p>
          </div>
        ) : (
          abTests.map((test) => <AbTestCard key={test.id} test={test} />)
        )}
      </section>
    </div>
  );
}
